import { Alert, App, Modal, Tree, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { TbFolder } from 'react-icons/tb'

import { classNames } from '@/Ship/Helpers/classNames'

import { itemsCount, storageTitle } from '../Helpers/diskFormat'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'

import type { DiskNode, DiskStorage } from '../Contracts/DiskNode'
import type { TreeDataNode } from 'antd'
import type { Key } from 'react'

import styles from './Disk.module.css'

const { Text } = Typography

/** Вантажити й створювати в корені простору може редактор і менеджер. */
const canWriteStorage = (storage: DiskStorage): boolean => storage.role === 'editor' || storage.role === 'manager'

/**
 * Модалка «Перемістити» / «Копіювати»: дерево просторів і папок, що
 * розгортається гілками (спільний кеш із лівою панеллю).
 *
 * Недоступні цілі — папки без права запису й самі папки, які переносять
 * (у себе чи у власну підпапку перенести не можна, тож їхні гілки не
 * розгортаються). Переміщення в інший простір дозволене лише менеджеру
 * вузла — це перевіряє бек і пояснює відмовою.
 *
 * Переміщення в інший простір — з підтвердженням (`DISK-005` U-26): доступи
 * прив'язані до вузла, а не до шляху (`DISK-001` §4.15.1), тож їдуть разом із
 * ним, а роль учасників рахується вже за новим простором.
 */
export const DiskTransferModal = observer(() => {
    const { message, modal } = App.useApp()
    const transfer = store.transfer
    const open = transfer !== null
    const nodes = transfer?.nodes ?? []
    const moving = transfer?.mode === 'move'
    const [target, setTarget] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<Key[]>([])
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) return

        const root = store.currentStorage?.rootFolderUuid ?? store.personalStorage?.rootFolderUuid

        setTarget(null)
        setError(null)
        setExpanded(root ? [root] : [])
        if (root) void store.tree.load(root)
    }, [open])

    const blocked = new Set(nodes.filter((node) => node.type === 'folder').map((node) => node.uuid))

    const toNode = (folder: DiskNode): TreeDataNode => {
        const children = store.tree.childrenOf(folder.uuid)
        const isBlocked = blocked.has(folder.uuid)

        return {
            key: folder.uuid,
            title: folder.name,
            icon: <TbFolder />,
            disabled: isBlocked || folder.permissions.write !== true,
            isLeaf: isBlocked || (children !== undefined && children.length === 0),
            ...(children && !isBlocked ? { children: children.map(toNode) } : {}),
        }
    }

    const treeData: TreeDataNode[] = store.storages.map((storage) => {
        const children = store.tree.childrenOf(storage.rootFolderUuid)

        return {
            key: storage.rootFolderUuid,
            title: storageTitle(storage),
            icon: <TbFolder />,
            disabled: !canWriteStorage(storage),
            isLeaf: children !== undefined && children.length === 0,
            ...(children ? { children: children.map(toNode) } : {}),
        }
    })

    /** Простір цільової папки: корінь — з переліку просторів, папка — з кешу дерева. */
    const targetStorage = (uuid: string): { uuid: string; title: string } | null => {
        const root = store.storages.find((storage) => storage.rootFolderUuid === uuid)
        if (root) return { uuid: root.uuid, title: storageTitle(root) }

        for (const children of store.tree.children.values()) {
            const folder = children.find((child) => child.uuid === uuid)
            if (folder?.storage) {
                const known = store.storageByUuid(folder.storage.uuid)

                return { uuid: folder.storage.uuid, title: known ? storageTitle(known) : folder.storage.title }
            }
        }

        return null
    }

    const confirmSubmit = (): void => {
        if (target === null || transfer === null) return

        const destination = moving ? targetStorage(target) : null
        const crossing =
            destination !== null && transfer.nodes.some((node) => node.storage !== null && node.storage.uuid !== destination.uuid)

        if (!crossing) {
            void submit()

            return
        }

        modal.confirm({
            title: `Перемістити в простір «${destination.title}»?`,
            content:
                'Доступи, надані на цих елементах, переїдуть разом із ними; учасники нового простору отримають свої права.',
            okText: 'Перемістити',
            cancelText: 'Скасувати',
            onOk: submit,
        })
    }

    const submit = async (): Promise<void> => {
        if (target === null || transfer === null) return

        const result = moving ? await store.move(transfer.nodes, target) : await store.copy(transfer.nodes, target)

        if (!result.ok) {
            setError(result.error)

            return
        }

        store.closeTransfer()
        void message.success(moving ? 'Переміщено' : 'Скопійовано')
    }

    const what = nodes.length === 1 ? `«${nodes[0]?.name ?? ''}»` : itemsCount(nodes.length)

    return (
        <Modal
            open={open}
            title={`${moving ? 'Перемістити' : 'Копіювати'} ${what}`}
            okText={moving ? 'Перемістити' : 'Копіювати'}
            cancelText="Скасувати"
            okButtonProps={{ disabled: target === null }}
            confirmLoading={store.isBusy}
            onOk={confirmSubmit}
            onCancel={() => store.closeTransfer()}
            destroyOnHidden
            width={520}
        >
            <Text type="secondary">Куди</Text>
            <div className={classNames(styles['transferTree'])} data-testid="disk-transfer-tree">
                <Tree
                    blockNode
                    showIcon
                    treeData={treeData}
                    expandedKeys={expanded}
                    onExpand={(keys) => setExpanded(keys)}
                    selectedKeys={target ? [target] : []}
                    onSelect={(keys) => {
                        setTarget(keys.length > 0 ? String(keys[0]) : null)
                        setError(null)
                    }}
                    loadData={async ({ key }) => {
                        await store.tree.load(String(key))
                    }}
                />
            </div>
            {error && <Alert type="error" showIcon title={error} style={{ marginTop: 12 }} />}
        </Modal>
    )
})
